import React, { useEffect, useState } from 'react'
import { api } from '../../lib/apiClient'
import { inviteUser, sendUserPasswordEmail } from '../../lib/userInvitations'
import { passwordEmailFailure } from '../../lib/userInvitationFlow.mjs'
import Button from '../ui/Button'
import Card, { CardHeader, CardTitle, CardDescription, CardContent } from '../ui/Card'
import Input from '../ui/Input'
import Icon from '../AppIcon'
import ConfirmModal from '../ui/ConfirmModal'

export default function UsersPanel(){
  const [rows, setRows] = useState([])
  const [email, setEmail] = useState('')
  const [newRole, setNewRole] = useState('')
  const [notice, setNotice] = useState('')
  const [pendingEmail, setPendingEmail] = useState(null)
  const [sendingEmails, setSendingEmails] = useState({})
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState({})
  const [deleteTarget, setDeleteTarget] = useState(null)
  const load = async()=> {
    try {
      const data = await api.listUsers()
      const users = (data.users || []).map(u => ({
        id: u.uid,
        email: u.email,
        role: (u.claims && u.claims.role) || '',
        disabled: !!u.disabled,
      }))
      setRows(users)
    } catch (e) {
      setRows([])
    }
  }
  useEffect(()=>{ load() }, [])

  const create = async()=>{
    if (!email) return; setSaving(true)
    try {
      setError('')
      setNotice('')
      const result = await inviteUser({ email, role: newRole })
      if (result.emailRequested) {
        setPendingEmail(null)
        setNotice(`Password setup email requested for ${result.user.email}. They choose their own password through that email.`)
      } else {
        setPendingEmail(result.user)
        setError(`Account created for ${result.user.email}. ${result.emailError}`)
      }
      setEmail('')
      setNewRole('')
      await load()
    } catch (err) {
      setError(err?.message || 'Failed to create user')
    } finally {
      setSaving(false)
    }
  }
  const sendPasswordEmail = async(user)=>{
    const id = user.uid || user.id
    if (!user.email || sendingEmails[id]) return
    setSendingEmails(prev => ({ ...prev, [id]: true }))
    setError('')
    setNotice('')
    try {
      await sendUserPasswordEmail(user.email)
      setNotice(`Password setup email requested for ${user.email}. They choose their own password through that email.`)
      setPendingEmail(current => current?.uid === id ? null : current)
    } catch (err) {
      setPendingEmail({ uid: id, email: user.email })
      setError(passwordEmailFailure(err))
    } finally {
      setSendingEmails(prev => ({ ...prev, [id]: false }))
    }
  }
  const setRole = async(id, role)=>{ await api.setRole({ uid:id, role }); await load() }
  const setDisabled = async(id, flag)=>{ await api.disableUser(id, flag); await load() }
  const removeUser = (id, emailAddr) => setDeleteTarget({ id, email: emailAddr })
  const confirmRemoveUser = async () => {
    const { id } = deleteTarget
    setDeleteTarget(null)
    setDeleting(prev => ({ ...prev, [id]: true }))
    try {
      await api.deleteUser(id)
      await load()
    } catch (err) {
      console.error('Delete user failed:', err)
      setError(err?.message || 'Failed to delete user')
    } finally {
      setDeleting(prev => ({ ...prev, [id]: false }))
    }
  }

  return (
    <Card variant="solid" hover="none">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Users & Roles</CardTitle>
            <CardDescription>Invite users by email and manage their access. Each person sets their own password privately.</CardDescription>
          </div>
          <Icon name="Users" size={24} className="text-primary" />
        </div>
      </CardHeader>
      <CardContent>
        {/* Add User Form */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Input
              label="New user email"
              type="email"
              autoComplete="email"
              placeholder="email@domain.com"
              value={email}
              onChange={e=>setEmail(e.target.value)}
              className="h-10"
            />
          </div>
          <select
            className="bg-white border border-border rounded-lg px-3 py-2 text-sm font-medium focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors duration-200"
            value={newRole}
            aria-label="New user role"
            onChange={e=>setNewRole(e.target.value)}
          >
            <option value="">Role (optional)</option>
            {['admin','staff','manager','office','accountant'].map(r=> <option key={r} value={r}>{r}</option>)}
          </select>
          <Button variant="default" size="default" iconName="Mail" onClick={create} loading={saving} disabled={saving || !email.trim()}>
            Create & send email
          </Button>
        </div>
        {notice && (
          <div role="status" className="mb-6 p-3 rounded-lg bg-white border border-blue-700 text-sm text-slate-950">
            {notice}
          </div>
        )}
        {error && (
          <div role="alert" className="mb-6 p-3 rounded-lg bg-white border border-red-700 text-sm text-red-800">
            {error}
            {pendingEmail && (
              <div className="mt-3">
                <Button variant="outline" size="sm" iconName="Mail" onClick={()=>sendPasswordEmail(pendingEmail)}
                  disabled={!!sendingEmails[pendingEmail.uid]}>
                  {sendingEmails[pendingEmail.uid] ? 'Requesting email...' : 'Send password email again'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Users Table (desktop) */}
        <div className="hidden md:block overflow-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/60">
              <tr className="text-foreground font-semibold">
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Role</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map(u=> (
                <tr key={u.id} className="hover:bg-muted/40 transition-colors">
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <Icon name="Mail" size={16} className="text-muted-foreground" />
                      <span className="font-medium">{maskEmail(u.email)}</span>
                    </div>
                  </td>
                  <td className="p-3">
                    <select
                      className="bg-white border border-border rounded-lg px-3 py-1.5 text-sm font-medium focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors duration-200"
                      value={u.role}
                      onChange={e=>setRole(u.id, e.target.value)}
                    >
                      <option value="">(none)</option>
                      {['admin','staff','manager','office','accountant'].map(r=> <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${u.disabled ? 'bg-destructive/10 text-destructive' : 'bg-success/10 text-success'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${u.disabled ? 'bg-destructive' : 'bg-success animate-pulse'}`} />
                      {u.disabled ? 'Disabled' : 'Active'}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="outline" size="sm" iconName="Mail" onClick={()=>sendPasswordEmail(u)}
                        disabled={u.disabled || !u.email || !!sendingEmails[u.id]}>
                        {sendingEmails[u.id] ? 'Requesting...' : 'Send password email'}
                      </Button>
                      <Button
                        variant={u.disabled ? "default" : "outline"}
                        size="sm"
                        iconName={u.disabled ? "Check" : "Ban"}
                        onClick={()=>setDisabled(u.id, !u.disabled)}
                      >
                        {u.disabled ? 'Enable' : 'Disable'}
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        iconName={deleting[u.id] ? "Loader2" : "Trash2"}
                        onClick={()=>removeUser(u.id, u.email)}
                        disabled={!!deleting[u.id]}
                      >
                        {deleting[u.id] ? 'Removing...' : 'Remove'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length===0 && (
                <tr>
                  <td colSpan={4} className="p-8 text-center">
                    <Icon name="Users" size={48} className="mx-auto text-muted-foreground/50 mb-3" />
                    <p className="text-muted-foreground">No users found</p>
                    <p className="text-sm text-muted-foreground/70 mt-1">Add a user to get started</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Users Cards (mobile) */}
        <div className="md:hidden space-y-3">
          {rows.map(u => (
            <div
              key={u.id}
              className="rounded-xl border border-border bg-card p-4 space-y-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Icon name="Mail" size={16} className="text-muted-foreground" />
                    <span className="font-medium text-sm truncate">{maskEmail(u.email)}</span>
                  </div>
                  <div className="mt-2">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${u.disabled ? 'bg-destructive/10 text-destructive' : 'bg-success/10 text-success'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${u.disabled ? 'bg-destructive' : 'bg-success animate-pulse'}`} />
                      {u.disabled ? 'Disabled' : 'Active'}
                    </span>
                  </div>
                </div>
                <Button
                  variant={u.disabled ? "default" : "outline"}
                  size="xs"
                  iconName={u.disabled ? "Check" : "Ban"}
                  onClick={()=>setDisabled(u.id, !u.disabled)}
                >
                  {u.disabled ? 'Enable' : 'Disable'}
                </Button>
              </div>

              <div>
                <label className="text-xs text-muted-foreground">Role</label>
                <select
                  className="mt-1 w-full bg-white border border-border rounded-lg px-3 py-2 text-sm font-medium focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors duration-200"
                  value={u.role}
                  onChange={e=>setRole(u.id, e.target.value)}
                >
                  <option value="">(none)</option>
                  {['admin','staff','manager','office','accountant'].map(r=> <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              <div className="flex items-center justify-between gap-2">
                <Button variant="outline" size="sm" iconName="Mail" onClick={()=>sendPasswordEmail(u)}
                  disabled={u.disabled || !u.email || !!sendingEmails[u.id]} className="flex-1">
                  {sendingEmails[u.id] ? 'Requesting...' : 'Send password email'}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  iconName={deleting[u.id] ? "Loader2" : "Trash2"}
                  onClick={()=>removeUser(u.id, u.email)}
                  disabled={!!deleting[u.id]}
                  className="flex-1"
                >
                  {deleting[u.id] ? 'Removing...' : 'Remove'}
                </Button>
              </div>
            </div>
          ))}
          {rows.length===0 && (
            <div className="rounded-xl border border-border bg-card p-8 text-center">
              <Icon name="Users" size={48} className="mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">No users found</p>
              <p className="text-sm text-muted-foreground/70 mt-1">Add a user to get started</p>
            </div>
          )}
        </div>

        {/* Stats */}
        {rows.length > 0 && (
          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <span>Total users: <span className="font-semibold text-foreground">{rows.length}</span></span>
            <span>Active: <span className="font-semibold text-success">{rows.filter(u => !u.disabled).length}</span></span>
          </div>
        )}
      </CardContent>

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete User"
        message={deleteTarget ? `Delete user ${deleteTarget.email}? This cannot be undone.` : ''}
        confirmLabel="Delete"
        variant="destructive"
        icon="Trash2"
        onConfirm={confirmRemoveUser}
        onCancel={() => setDeleteTarget(null)}
      />
    </Card>
  )
}

function maskEmail(e){ if (!e) return ''; const [a,b]=String(e).split('@'); return a[0]+'***@'+(b||'***') }
